
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../supabase';
import { showToast } from '../../../constants';
import { queryKeys } from '../../../lib/queryKeys';
import { supabaseQuery } from '../../../lib/supabaseQuery';
import { useFilterParam } from '../../../hooks/usePersistedFilters';

/**
 * Hook for the reception shift handover (Schichtübergabe).
 *
 * Loads handover reports for the tenant and exposes the full lifecycle:
 * create (draft with items) → submit → acknowledge, plus draft editing,
 * item tick-off and draft deletion.
 *
 * Data layer conventions (AGENTS.md §2.1):
 *   - Server state via useQuery + supabaseQuery (reads carry .limit())
 *   - Mutations are direct supabase calls + queryClient.invalidateQueries
 *   - Realtime invalidation is centralized in src/lib/realtimeStore.ts
 *
 * @param {Object|null} user - Current authenticated user (needs tenantId, id, role).
 * @returns {{
 *   handovers: Object[],
 *   incoming: Object[],   — unacknowledged handovers addressed to me
 *   outgoingDraft: Object|null — my current draft (if any)
 *   myRole: string,
 *   canCreate: boolean,
 *   filterLocation: string, — active location filter ('' = all)
 *   setFilterLocation: (v: string) => void,
 *   createHandover: (payload) => Promise<string>,
 *   updateDraft: (handoverId, patch) => Promise<void>,
 *   submitHandover: (handoverId) => Promise<void>,
 *   acknowledgeHandover: (handoverId) => Promise<void>,
 *   deleteDraft: (handoverId) => Promise<void>,
 *   addItem: (handoverId, item) => Promise<void>,
 *   removeItem: (itemId) => Promise<void>,
 *   tickItem: (handoverId, itemId, isDone) => Promise<void>,
 * }}
 */
interface HandoverUser {
    tenantId?: string;
    id?: string;
    role?: string;
}

export interface HandoverItemInput {
    item_type: 'info' | 'task' | 'incident';
    content: string;
    room_ref?: string | null;
}

export interface CreateHandoverPayload {
    handover_date: string;
    shift_label: 'frueh' | 'spaet' | 'nacht' | 'andere';
    property_id?: string | null;
    incoming_user_id: string | null;
    general_notes: string | null;
    items: HandoverItemInput[];
}

/**
 * 2026-10-08 owner decision: the Schichtübergabe (shift handover) is usable by
 * ALL staff roles — viewing AND creating. Guests, the pending placeholder role
 * and accountants stay excluded from CREATE (accountant remains DB read-only
 * per the C-2 hardening; the pending role is the pre-approval placeholder).
 * RLS mirrors this list in migration 20261008120000.
 */
const HANDOVER_CREATE_ROLES = [
    'admin', 'management', 'reception', 'hausdame', 'ht_leitung',
    'sales', 'haustechnik', 'housekeeping', 'rads',
];
const LIST_LIMIT = 50; // AGENTS.md §1: default list cap

export function useHandovers(user: HandoverUser | null) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const activeTenantId: string = user?.tenantId ?? '';
    const myId: string = user?.id ?? '';
    const myRole: string = user?.role ?? '';
    // Field kept named `canCreate` (was `isReception` before the all-roles
    // decision): true for every staff role, false for guest/pending/accountant.
    const canCreate = HANDOVER_CREATE_ROLES.includes(myRole);

    // ── Location filter (feature-local, URL-persisted via useFilterParam) ──
    // The value is a single Apaleo property id; '' = all locations. The
    // server-side .eq() is applied ONLY when a location is chosen, so the
    // filter is inert until the property_id migration is applied.
    const [filterLocation, setFilterLocation] = useFilterParam<string>('handover', 'location', '');

    // ── Server state: handover list ────────────────────────────────
    const { data: handovers, isLoading } = useQuery({
        queryKey: queryKeys.handover.list(activeTenantId, filterLocation),
        queryFn: supabaseQuery(db => {
            let q = db.from('shift_handovers')
                .select('*')
                .eq('tenant_id', activeTenantId);
            if (filterLocation) q = q.eq('property_id', filterLocation);
            return q
                .order('handover_date', { ascending: false })
                .order('created_at', { ascending: false })
                .limit(LIST_LIMIT);
        }),
        enabled: !!activeTenantId,
    });

    // Realtime handled by centralized realtimeStore (src/lib/realtimeStore.ts)

    // ── Derived state ──────────────────────────────────────────────
    const rows = useMemo(() => handovers ?? [], [handovers]);

    const incoming = useMemo(
        () => rows.filter(h => h.incoming_user_id === myId && h.status === 'submitted'),
        [rows, myId],
    );

    const outgoingDraft = useMemo(
        () => rows.find(h => h.outgoing_user_id === myId && h.status === 'draft') ?? null,
        [rows, myId],
    );

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: queryKeys.handover.all });

    // ── Mutations ──────────────────────────────────────────────────

    /** Creates a draft handover together with its initial items. Returns the new id. */
    async function createHandover(payload: CreateHandoverPayload): Promise<string> {
        const { data, error } = await supabase.from('shift_handovers').insert({
            tenant_id: activeTenantId,
            handover_date: payload.handover_date,
            shift_label: payload.shift_label,
            property_id: payload.property_id || null,
            outgoing_user_id: myId,
            incoming_user_id: payload.incoming_user_id || null,
            status: 'draft',
            general_notes: payload.general_notes,
        }).select('id').single();
        if (error) throw error;

        if (payload.items.length > 0) {
            const { error: itemsError } = await supabase.from('shift_handover_items')
                .insert(payload.items.map(it => ({
                    tenant_id: activeTenantId,
                    handover_id: data.id,
                    item_type: it.item_type,
                    content: it.content,
                    room_ref: it.room_ref || null,
                })));
            if (itemsError) throw itemsError;
        }
        await invalidate();
        showToast(t('handover.toasts.created'), 'success');
        return data.id;
    }

    /** Edits a draft (only valid while status = 'draft', enforced by RLS). */
    async function updateDraft(handoverId: string, patch: Record<string, unknown>) {
        const { error } = await supabase.from('shift_handovers')
            .update(patch)
            .eq('id', handoverId)
            .eq('tenant_id', activeTenantId)
            .eq('status', 'draft');
        if (error) throw error;
        await invalidate();
    }

    /** Submits a draft → immutable for the outgoing user, visible to incoming. */
    async function submitHandover(handoverId: string) {
        const { error } = await supabase.from('shift_handovers')
            .update({ status: 'submitted', submitted_at: new Date().toISOString() })
            .eq('id', handoverId)
            .eq('tenant_id', activeTenantId)
            .eq('status', 'draft');
        if (error) throw error;
        await invalidate();
        showToast(t('handover.toasts.submitted'), 'success');
    }

    /**
     * Acknowledges (completes) the handover (submit → acknowledged).
     * TWO RLS paths (migration 20260920120000):
     *   - the named incoming user acknowledges (original path), or
     *   - the CREATOR completes their own handover when NO successor was
     *     named (creator-completion — this closes the gap where a handover
     *     created without an incoming user could never leave 'submitted').
     */
    async function acknowledgeHandover(handoverId: string) {
        const target = rows.find(h => h.id === handoverId);
        const asCreator = !!target
            && target.outgoing_user_id === myId
            && !target.incoming_user_id;

        let builder = supabase.from('shift_handovers')
            .update({
                status: 'acknowledged',
                acknowledged_at: new Date().toISOString(),
                acknowledged_by: myId,
            })
            .eq('id', handoverId)
            .eq('tenant_id', activeTenantId)
            .eq('status', 'submitted');
        builder = asCreator
            ? builder.is('incoming_user_id', null).eq('outgoing_user_id', myId)
            : builder.eq('incoming_user_id', myId);

        const { error } = await builder;
        if (error) throw error;
        await invalidate();
        showToast(t('handover.toasts.acknowledged'), 'success');
    }

    /** Deletes a draft (drafts only — enforced by RLS). */
    async function deleteDraft(handoverId: string) {
        const { error } = await supabase.from('shift_handovers')
            .delete()
            .eq('id', handoverId)
            .eq('tenant_id', activeTenantId)
            .eq('status', 'draft');
        if (error) throw error;
        await invalidate();
        showToast(t('handover.toasts.deleted'), 'success');
    }

    /** Adds one item to a DRAFT handover (insert guard blocks post-submit adds). */
    async function addItem(handoverId: string, item: HandoverItemInput) {
        const { error } = await supabase.from('shift_handover_items').insert({
            tenant_id: activeTenantId,
            handover_id: handoverId,
            item_type: item.item_type,
            content: item.content,
            room_ref: item.room_ref || null,
        });
        if (error) throw error;
        await invalidate();
    }

    /** Removes one item from a DRAFT handover. */
    async function removeItem(itemId: string) {
        const { error } = await supabase.from('shift_handover_items')
            .delete()
            .eq('id', itemId)
            .eq('tenant_id', activeTenantId);
        if (error) throw error;
        await invalidate();
    }

    /** Incoming user ticks an item off/on (allowed while submitted / ≤24h post-ack). */
    async function tickItem(handoverId: string, itemId: string, isDone: boolean) {
        const { error } = await supabase.from('shift_handover_items')
            .update({
                is_done: isDone,
                done_by: isDone ? myId : null,
                done_at: isDone ? new Date().toISOString() : null,
            })
            .eq('id', itemId)
            .eq('tenant_id', activeTenantId)
            .eq('handover_id', handoverId);
        if (error) throw error;
        await invalidate();
    }

    return {
        handovers: rows,
        isLoading,
        incoming,
        outgoingDraft,
        myRole,
        canCreate,
        filterLocation,
        setFilterLocation,
        createHandover,
        updateDraft,
        submitHandover,
        acknowledgeHandover,
        deleteDraft,
        addItem,
        removeItem,
        tickItem,
    };
}

/**
 * Hook loading the items of ONE handover. Kept separate so list views do not
 * fetch item rows for all 50 handovers at once.
 */
export function useHandoverItems(handoverId: string | null) {
    const { data: items } = useQuery({
        queryKey: queryKeys.handover.items(handoverId ?? ''),
        queryFn: supabaseQuery(db => db.from('shift_handover_items')
            .select('*')
            .eq('handover_id', handoverId!)
            .order('created_at', { ascending: true })
            .limit(200)),
        enabled: !!handoverId,
    });
    return { items: items ?? [] };
}

